import React, { useState, useEffect } from 'react';
import { loadLanguagePack } from '@americanexpress/one-app-ducks';
import oneAppModuleWrapper from '@americanexpress/one-app-module-wrapper';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { composeModules, RenderModule } from 'holocron';
import { IntlProvider, FormattedMessage } from 'react-intl';
import { useRouter } from '@americanexpress/one-app-router';
import {
  ErrorView,
  DEFAULT_ANALYTICS_PAGE_VIEW,
  BASE_UI_URL,
  FallbackRender,
} from '@americanexpress/arcade-core';
// import { fromJS } from 'immutable';
import { useUpdateView } from '@americanexpress/data-manager-utils';
import { useReactlytics } from '@americanexpress/reactlytics';
import { apiGetUserStatus } from '../api';
import RenderArcadeChildren from './RenderArcadeChildren';
import { shouldHideNavbar } from './utils/wrapperUtils';
import styles from './styles/renderArcadeChildren.scss';

const ArcadePageWrapper = ({
  children, router, languageData, locale,
}) => {
  const userStatus = useSelector((state) => state.getIn(['modules', 'arcade-root', 'common', 'userStatus']));
  const isMobile = useSelector((state) => state.getIn(['modules', 'arcade-root', 'common', 'isMobile']));
  const isProfileStatusApiError = useSelector((state) => state.getIn(['modules', 'arcade-root', 'common', 'isProfileStatusApiError']));
  const isProfileStatusLoading = useSelector((state) => state.getIn(['modules', 'arcade-root', 'common', 'isProfileStatusLoading']));
  const rootTenantUrl = useSelector((state) => state.getIn(['config', 'rootTenantUrl']));
  const akamaiTenantUrl = useSelector((state) => state.getIn(['config', 'akamaiTenantUrl']));
  const gemUrl = useSelector((state) => state.getIn(['config', 'GemUrl']));
  const errorBackground = `${gemUrl}/Images/ErrorBackground.png`;
  const dispatch = useDispatch();
  const updateView = useUpdateView();
  const { track } = useReactlytics();

  const [showNavbar, setshowNavbar] = useState(true);
  const [isNative, setIsNative] = useState(false);
  const [isFaq, setIsFaq] = useState(false);

  const { pathname, query } = router.location;
  const destPage = global.BROWSER && window.location.hostname.includes('passport')
    ? `${rootTenantUrl}`
    : `${akamaiTenantUrl}`;

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  const prevUserStatus = global.BROWSER && localStorage.getItem('prevUserStatus');

  useEffect(() => {
    const parsedLocalUserStatus = prevUserStatus ? JSON.parse(prevUserStatus) : null;
    if (userStatus === 'not_enrolled' || userStatus === 'unenrolled') {
      localStorage.setItem('prevUserStatus', JSON.stringify({
        userStatus,
        isFirstTimeUser: true,
      }));
    } else if (userStatus === 'enrolled' && parsedLocalUserStatus?.userStatus !== 'enrolled') {
      localStorage.setItem('prevUserStatus', JSON.stringify({
        userStatus,
        isFirstTimeUser: true,
      }));
    } else if (
      (userStatus === 'unauthorized' || query?.native === 'true')
      && parsedLocalUserStatus?.userStatus === 'enrolled'
    ) {
      localStorage.setItem('prevUserStatus', JSON.stringify({
        userStatus: 'enrolled',
        isFirstTimeUser: false,
      }));
    }

    const isBot = /bot|crawl|slurp|spider|mediapartners/i.test(navigator.userAgent);
    if (!userStatus) {
      //  TODO: comment for local development
      if (isBot) {
        router.replace(`${BASE_UI_URL}/landing/`);
      } else {
        dispatch(apiGetUserStatus());
      }

      // ************************************************
      // TODO: uncomment for local development
      // setTimeout(
      //   () => dispatch({
      //     type: 'modules/arcade-root/MERGE_VALUES',
      //     payload: fromJS({
      //       userStatus: 'enrolled',
      //       canProfileRefresh: false,
      //     }),
      //   }),
      //   500
      // );
      // ************************************************
    }
  }, [prevUserStatus?.isFirstTimeUser, userStatus]);

  useEffect(() => {
    if (!isFaq) {
      window?.screen?.orientation?.addEventListener('change', scrollToTop);
      return () => window?.screen?.orientation?.removeEventListener('change', scrollToTop);
    } return null;
  }, [isFaq]);

  useEffect(() => {
    switch (true) {
      case !!query?.faq:
        setIsFaq(query?.faq === 'true');
        break;

      case !!query?.native:
        sessionStorage.setItem('isNative', query.native);
        setIsNative(query.native === 'true');
        break;

      default:
        setIsFaq(false);
        setIsNative(sessionStorage.getItem('isNative') === 'true');
        break;
    }
  }, [query]);

  useEffect(() => {
    if (!isFaq) {
      scrollToTop();
    }
    setshowNavbar(!shouldHideNavbar(pathname));
  }, [pathname, isFaq]);

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  const handleProfileStatusRetry = () => {
    track({
      event: {
        identifier: 'axp-passport-tech-error',
        element: 'try-again',
        type: 'click',
      },
    });
    dispatch(apiGetUserStatus());
  };

  const handleBack = () => {
    track({
      event: {
        identifier: 'axp-passport-tech-error',
        element: 'back',
        type: 'click',
      },
    });
    router.goBack();
  };

  const renderViews = () => {
    if (userStatus === 'resource_not_found') {
      return (
        <ErrorView
          isNative={isNative}
          isFullScreen={true}
          backgroundImageUrl={errorBackground}
          description="Account is not allowlisted."
        />
      );
    }

    if (isProfileStatusApiError && userStatus === null) {
      updateView({ viewId: `${DEFAULT_ANALYTICS_PAGE_VIEW}|tech-error` });
      return (
        <ErrorView
          isNative={isNative}
          isFullScreen={true}
          backgroundImageUrl={errorBackground}
          description={<FormattedMessage id="profile.status.error" defaultMessage="We're currently facing some technical difficulties. Please try again." />}
          onBackBtnClick={handleBack}
          onButtonClick={handleProfileStatusRetry}
          isLoading={isProfileStatusLoading}
        />
      );
    }

    return (
      <RenderArcadeChildren
        isMobile={isMobile}
        isNative={isNative}
        router={router}
        showNavbar={showNavbar}
        pathname={pathname}
      >
        { children }
      </RenderArcadeChildren>
    );
  };

  const canRenderHeaderFooter = !isNative && !pathname.includes('/enroll');

  return (
    <IntlProvider locale={locale} messages={languageData}>
      <div
        className={`height-full ${showNavbar ? styles.mainContainerWrapperWithNavbar : ''}`}
        data-testid="arcade-page-wrapper"
      >
        {canRenderHeaderFooter
          && (
            <RenderModule
              moduleName="axp-global-header"
              props={{
                logoutDestPage: destPage,
                loginDestPage: destPage,
              }}
            />
          )}
        {renderViews()}
        {canRenderHeaderFooter && <RenderModule moduleName="axp-footer" />}
      </div>
    </IntlProvider>
  );
};

export const TestableArcadePageWrapper = ArcadePageWrapper;

ArcadePageWrapper.propTypes = {
  children: PropTypes.element,
  router: PropTypes.shape({
    location: PropTypes.shape({
      pathname: PropTypes.string,
      query: PropTypes.shape({
        native: PropTypes.string,
        faq: PropTypes.string,
      }),
    }),
    push: PropTypes.func,
    replace: PropTypes.func,
    goBack: PropTypes.func,
  }).isRequired,
  languageData: PropTypes.shape({}).isRequired, // no need to restate all the keys in the lang pack
  locale: PropTypes.string.isRequired,
};

export const loadModuleData = async ({ store: { dispatch } }) => dispatch(
  loadLanguagePack('arcade-page-wrapper', { fallbackLocale: 'en-US' }),
  await dispatch(composeModules([{ name: 'axp-global-header' }, { name: 'axp-footer' }]))
);

ArcadePageWrapper.holocron = {
  name: 'arcade-page-wrapper',
  loadModuleData,
};

/* istanbul ignore next */
const CustomErrorView = () => {
  const history = useRouter();
  const { track } = useReactlytics();
  const isNative = global.BROWSER && sessionStorage.getItem('isNative') === 'true';

  const handleBack = () => {
    track({ event: { identifier: 'axp-passport-stamp-error', element: 'back-button', type: 'click' } });
    history.goBack();
  };

  return (
    <FallbackRender
      onBackBtnClick={handleBack}
      isNative={isNative}
      description="We're currently facing some technical difficulties. Please try again."
      isFullScreen={true}
    />
  );
};

/* istanbul ignore next */
export default oneAppModuleWrapper('arcade-page-wrapper', {
  fallbackRender: () => <CustomErrorView />,
})(ArcadePageWrapper);
